import { useState } from "react";
import { useTranslation } from "react-i18next";

import { defaultUsageFilters } from "../../../redux/feature-usage/feature-usage.api";
import { useUsageUnused } from "../../../redux/feature-usage/feature-usage.selectors";
import { formatCount, formatDate, formatPercent } from "../utils/featureUsage";
import { MetricTitle } from "./MetricHelp";
import { UsageFilters } from "./UsageFilters";
import { FeatureCell, UsageColumn, UsageStatus, UsageSummary, UsageTable } from "./UsageTable";

import type { ProductUsageFilters, ProductUsageUnusedRow } from "@signalist/shared-types";

export function UnusedFeaturesTab() {
	const { t } = useTranslation();
	const [filters, setFilters] = useState<ProductUsageFilters>(defaultUsageFilters);
	const query = useUsageUnused(filters);
	const { data } = query;
	const columns: UsageColumn<ProductUsageUnusedRow>[] = [
		{
			key: "feature",
			title: <MetricTitle metric="feature" />,
			render: (row) => <FeatureCell feature={row} />,
		},
		{
			key: "flagOn",
			title: <MetricTitle metric="flagOn" />,
			render: (row) => (
				<>
					{row.flagOnCompanies === null ? t("featureUsage.dash") : formatCount(row.flagOnCompanies)}
					<div className="text-xs text-text-meta">{row.featureCode}</div>
				</>
			),
		},
		{
			key: "companiesUsed",
			title: <MetricTitle metric="companiesUsed" />,
			render: (row) => formatCount(row.companiesUsed),
		},
		{
			key: "lastEvent",
			title: <MetricTitle metric="lastEvent" />,
			render: (row) =>
				row.lastEventDate === null ? t("featureUsage.noHistory") : formatDate(row.lastEventDate),
		},
		{
			key: "reason",
			title: (
				<MetricTitle
					metric="reason"
					values={{ threshold: formatPercent(data?.nearZeroAdoptionShare ?? 0.01) }}
				/>
			),
			render: (row) => (
				<div className="flex flex-wrap gap-1">
					{row.reasons.map((reason) => (
						<span
							key={reason}
							className={`rounded-full px-2 py-1 text-xs ${reason === "near-zero" ? "bg-status-warning/10 text-status-warning" : "bg-brand-light/5 text-text-meta"}`}
						>
							{t(`featureUsage.reasons.${reason}`)}
						</span>
					))}
				</div>
			),
		},
		{
			key: "sample",
			title: <MetricTitle metric="sample" />,
			render: (row) =>
				row.flagOnNeverUsedCompanies > 0
					? t("featureUsage.sample", {
							count: row.flagOnNeverUsedCompanies,
							names: row.flagOnNeverUsedSample.map((company) => company.name).join(", "),
						})
					: t("featureUsage.dash"),
		},
	];

	return (
		<>
			<UsageFilters plans={data?.cohort.plans ?? []} value={filters} onChange={setFilters} />
			<UsageStatus {...query} retry={query.refetch} />
			{data && !query.isError && !query.isLoading && data.lastRolledUpDate !== null && (
				<>
					<UsageSummary data={data} />
					<h3 className="mb-4 text-sm font-semibold text-text">
						{t("featureUsage.unusedHeading")}
					</h3>
					<UsageTable areas={data.areas} columns={columns} label={t("featureUsage.tabs.unused")} />
					<p className="mt-4 rounded bg-brand-light/5 p-3 text-xs text-text-meta">
						{t("featureUsage.historyNote")}
					</p>
				</>
			)}
		</>
	);
}
